import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchLeadersQualification = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, leaderCriteriaId } = useParams();

  return useQuery({
    queryKey: ["leadersQualification", organizationId, leaderCriteriaId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/statistics/leaders/criteria/${leaderCriteriaId}`,
      });
      return result?.data?.[0];
    },
    enabled: !!leaderCriteriaId,
  });
};

export const useFetchLeadersQualificationsPagination = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/statistics/leaders/criteria`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["leadersQualification", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useFetchLeadersQualifications = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/statistics/leaders/criteria`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["leadersQualification", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useDeleteLeadersQualification = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (leaderCriteriaId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/statistics/leaders/criteria/${leaderCriteriaId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "leaders.qualification.delete.success",
          defaultMessage: "Leaders Qualification deleted successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({
        queryKey: ["leadersQualification"],
      });
    },
  });
};

export const useCreateLeadersQualification = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/statistics/leaders/criteria`,
        method: "POST",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "site.create.success",
          defaultMessage: "Leaders Qualification Template created successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({
        queryKey: ["leadersQualification"],
      });
    },
  });
};

export const useUpdateLeadersQualification = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ leaderCriteriaId, data }) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/statistics/leaders/criteria/${leaderCriteriaId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "leaders.qualification.update.success",
          defaultMessage: "Leaders Qualification Template updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({
        queryKey: ["leadersQualification"],
      });
    },
  });
};
